import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePaystackPayment } from 'react-paystack';
import { toast } from 'react-toastify';
import api from '../api';
import { updateStudents } from '../redux/slices/guardianSlice';
import { updatePaymentDetails } from '../redux/slices/accountSlice';
import { DISCOUNT_CODES } from '../utils/constants';

const PAYSTACK_PUBLIC_KEY = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;

const usePaystackUpgrade = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const account = useSelector((state) => state.account);
    const guardian = useSelector((state) => state.guardian);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [showConfirmPaymentDialog, setShowConfirmPaymentDialog] = useState(false);
    const [discountCode, setDiscountCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentStudent, setCurrentStudent] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState(1); // Default to 1 month
    const [baseAmount, setBaseAmount] = useState(Number(account.paymentDetails?.payment_setting?.charge_amount) * 100 || 4000); // Base amount per month in pesewas (GHC 40 * 100)

    const getDiscountedAmount = (code, duration) => {
        const upperCode = code.toUpperCase();
        const discountPercentage = DISCOUNT_CODES[upperCode] || 0;
        const totalBaseAmount = baseAmount * duration; // Total before discount
        return Math.round(totalBaseAmount * (1 - discountPercentage / 100));
    };

    const getDiscountAmount = useCallback((code, duration) => {
        const upperCode = code.toUpperCase();
        const discountPercentage = DISCOUNT_CODES[upperCode] || 0;
        const totalBaseAmount = baseAmount * duration;
        return Math.round(totalBaseAmount * (discountPercentage / 100));
    }, []);

    const isTrialCode = (code) => code.toUpperCase() === 'TRIAL';

    useEffect(() => {
        console.log('Current student:', currentStudent);
        if (currentStudent?.payerType === 'guardian') {
            setBaseAmount(currentStudent.school.payment_setting.charge_amount * 100 || 4000); // Use guardian's school charge
        }
    }, [currentStudent])

    const handleSubscribe = async (reference, student) => {
        try {
            const isGuardianPayment = student.payerType === 'guardian';
            const paymentData = {
                student_id: student.id,
                received_from_id: isGuardianPayment ? guardian.details.id : account.details.id,
                received_from_type: isGuardianPayment ? 'App\\Models\\Guardian' : 'App\\Models\\Student',
                payment_method: 'Paystack',
                payment_details: JSON.stringify(reference),
                discount_amount: student.discountAmount,
                discount_code: student.discountCode,
                amount: student.amount, // Total amount including duration
                duration: selectedDuration, // Include duration in payment data
            };

            const response = await api.post('/make-payment', paymentData);
            if (isGuardianPayment) {
                const updatedStudents = guardian.students.map((s) =>
                    s.id === student.id ? { ...s, payment_details: response.data.payment_details } : s
                );
                dispatch(updateStudents(updatedStudents));
            } else {
                dispatch(updatePaymentDetails(response.data.payment_details));
            }
            toast.success("Account upgraded successfully", { position: "bottom-right" });
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Payment error:', error);
            toast.error("Failed to subscribe to Advantage plan", { position: "bottom-right" });
            return { success: false, error };
        }
    };

    const applyDiscountCode = useCallback(async () => {
        if (isLoading || !currentStudent) return;
        setIsLoading(true);

        const upperCode = discountCode.toUpperCase();
        const finalAmount = discountCode && discountCode !== 'SHOW'
            ? getDiscountedAmount(discountCode, selectedDuration)
            : baseAmount * selectedDuration; // Multiply base amount by duration
        const discountAmount = discountCode && discountCode !== 'SHOW'
            ? getDiscountAmount(discountCode, selectedDuration)
            : 0;

        const studentData = {
            ...currentStudent,
            subscriptionAmount: finalAmount,
            discountAmount: discountAmount,
            discountCode: discountCode && discountCode !== 'SHOW' ? upperCode : null,
            amount: finalAmount, // Total amount for payment
            // duration: selectedDuration, // Add duration to student data
        };

        try {
            if (isTrialCode(discountCode)) {
                const response = await api.post('/trial-payment', {
                    student_id: studentData.id,
                    received_from_id: account.details.id,
                    received_from_type: studentData.payerType === 'guardian' ? 'App\\Models\\Guardian' : 'App\\Models\\Student',
                    discount_code: studentData.discountCode,
                    discount_amount: studentData.discountAmount,
                    amount: baseAmount * selectedDuration, // Use duration for trial too
                    type: 'paid_trial',
                });
                if (studentData.payerType === 'guardian') {
                    const updatedStudents = guardian.students.map(student =>
                        student.id === studentData.id
                            ? { ...student, payment_details: response.data.payment_details }
                            : student
                    );
                    dispatch(updateStudents(updatedStudents));
                } else {
                    dispatch(updatePaymentDetails(response.data.payment_details));
                }
                toast.success("Trial subscription activated successfully", { position: "bottom-right" });
            } else {
                if (discountCode && discountCode !== 'SHOW' && !DISCOUNT_CODES[upperCode]) {
                    toast.error("Invalid discount code", { position: "bottom-right" });
                } else {
                    if (DISCOUNT_CODES[upperCode]) {
                        toast.success(`Discount of ${DISCOUNT_CODES[upperCode]}% applied`, { position: "bottom-right" });
                    }
                    const paystackProps = {
                        reference: new Date().getTime().toString(),
                        currency: 'GHS',
                        email: user.details.email || studentData.user.email,
                        amount: finalAmount, // Convert to kobo/cedis (Paystack expects this)
                        publicKey: PAYSTACK_PUBLIC_KEY,
                    };
                    const initializePayment = usePaystackPayment(paystackProps);
                    initializePayment({
                        email: studentData.user?.email || user.details.email,
                        amount: finalAmount, // Convert to kobo/cedis
                        onSuccess: (reference) => handleSubscribe(reference, studentData),
                        onClose: () => toast.info('Payment cancelled', { position: "bottom-right" }),
                    });
                }
            }
            setShowConfirmPaymentDialog(false);
            setDiscountCode('');
            setSelectedDuration(1); // Reset to default after payment
        } catch (error) {
            console.error('Payment error:', error);
            toast.error(isTrialCode(discountCode) ? "Failed to activate trial" : "Payment processing failed", { position: "bottom-right" });
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, currentStudent, discountCode, selectedDuration, user.details, dispatch]);

    const upgradeStudent = (student) => {
        console.log('upgrading student', student);
        setCurrentStudent(student);
        if (!user.details.email) {
            setShowEmailDialog(true);
            console.log('upgrading student', student);
            return;
        }
        setShowConfirmPaymentDialog(true);
    };

    const handleConfirmPaymentOpenChange = (open) => {
        if (!open && !isLoading) {
            setShowConfirmPaymentDialog(false);
            setDiscountCode('');
            setSelectedDuration(1); // Reset duration when closing
        }
    };

    return {
        upgradeStudent,
        showEmailDialog,
        setShowEmailDialog,
        showConfirmPaymentDialog,
        setShowConfirmPaymentDialog,
        discountCode,
        setDiscountCode,
        isLoading,
        applyDiscountCode,
        handleConfirmPaymentOpenChange,
        selectedDuration, // Expose selectedDuration
        setSelectedDuration, // Expose setter for duration
        baseAmount,
    };
};

export default usePaystackUpgrade;